import { useDraggable } from "@dnd-kit/react";
import React, { useState } from "react";
import { classNames } from "src/common_util";
import { GroupedDelivery, MaplinkNode } from "./delivery";
import { useDashboardConfig } from "./dispatch_api_client";
import { Dashboard, Delivery, DeliveryGroup, DeliveryGroupStatus } from "./model";
import { DeliveryGroupSelect, DriverSelect } from "./selects";

const GroupComponent = ({
  dashboard,
  deliveries,
  group,
}: {
  dashboard: Dashboard;
  deliveries: Delivery[];
  group: DeliveryGroup;
}): React.JSX.Element => {
  const { apiClient } = useDashboardConfig();
  const [dropping, setDropping] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorTimerId, setErrorTimerId] = useState(0);

  const handleDropping = () => {
    setDropping(true);
  }

  const handleDragFailure = () => {
    setDropping(false);
    const errorTimer = window.setTimeout(() => setHasError(false), 1500);
    setErrorTimerId(errorTimer);
    setHasError(true);
  }

  const unsetErrorTimer = () => {
    window.clearTimeout(errorTimerId);
    setErrorTimerId(0);
    setHasError(false);
  }

  const { ref, isDragging } = useDraggable({
    id: `group:${group.id}`,
    data: {
      id: group.id,
      type: "group",
      handleDropping,
      handleDragFailure,
      unsetErrorTimer
    },
    disabled: dropping
  });

  const style = isDragging ? {
    opacity: 0.3
  } : undefined;

  const readyToAssign = group.status === DeliveryGroupStatus.ReadyToAssign;

  const classes = classNames({
    "is-dragged-node": isDragging,
    "has-drag-error": hasError,
    "is-half-opacity": dropping,
    "is-ready-to-assign": readyToAssign
  });

  const handleAllocateGroupToDriver = async (driverId: number) => {
    await dashboard.handleAllocateGroupToDriver(group.id, driverId, apiClient);
  }

  const handleAssociateGroupToDriver = async (driverId: number) => {
    await apiClient.associateDriverToGroup(group.id, driverId > 0 ? driverId : null);
  }

  const handleMergeGroups = async (toGroupId: number) => {
    if (toGroupId == -2) return;
    if (toGroupId == -1) {
      await apiClient.unGroup(group.id);
    } else {
      await apiClient.mergeGroups(group.id, toGroupId);
    }
  }

  const handleReadyToggle = async () => {
    if (readyToAssign) {
      await apiClient.unreadyGroup(group.id);
    } else {
      await apiClient.readyGroup(group.id);
    }
  }

  const handleLockToggle = async () => {
    if (group.locked) {
      await apiClient.unlockGroup(group.id);
    } else {
      await apiClient.lockGroup(group.id);
    }
  }

  const filterDeliveryGroups = (groups: DeliveryGroup[], groupId: number): DeliveryGroup[] => {
    return groups.filter((ele) => ele.id != groupId);
  }

  const availableDrivers = dashboard.availableDriversForDeliveries(deliveries);

  const lockedIcon = group.locked ? "lock" : "lock-open";

  const allowAllocation = deliveries.every((delivery) => delivery.allowAllocation);

  const readyIcon = readyToAssign ? "fas fa-circle-check" : "far fa-circle-check";

  // A group holding a delivery that can't be assigned can't be readied, but must stay unreadyable
  const readyToggle = (readyToAssign || allowAllocation) ?
    <div className="card-header-icon pr-3 pl-1 p-1">
      <div className="icon is-small ready-group">
        <a onClick={() => void handleReadyToggle()} title={readyToAssign ? "Mark as not ready" : "Mark as ready to assign"}>
          <i className={readyIcon}></i>
        </a>
      </div>
    </div>
    : <></>;

  const maplink = deliveries.length >= 2 ?
    <div className="card-header-icon p-1">
      <MaplinkNode deliveries={deliveries} />
    </div>
    : <></>;

  const deliveryGroupDeliveries = deliveries.map(delivery => {
    return (
      <GroupedDelivery
        key={delivery.id}
        dashboard={dashboard}
        defaultExpand={false}
        delivery={delivery}
        deliveringNow={false}
      />
    )
  });

  const driverSelect = (availableDrivers.length > 0 && allowAllocation) ?
    <DriverSelect storedDriverId={group.selectedDriverId} drivers={availableDrivers} allowUnallocate={false} onConfirm={handleAllocateGroupToDriver} onSelect={handleAssociateGroupToDriver} /> : <></>

  return (
    <div className={`card is-card-dashboard ${classes}`} style={style} ref={ref}>
      <header className="card-header">
        <div className="card-header-title px-1 py-1">
          <span className="is-size-7 has-text-weight-medium p-2">
            {group.groupNumber}
          </span>
        </div>

        {maplink}

        {readyToggle}
        <div className="card-header-icon pr-3 pl-1 p-1">
          <div className="icon is-small lock-group">
            <a onClick={() => void handleLockToggle()}>
              <i className={`fas fa-${lockedIcon}`}></i>
            </a>
          </div>
        </div>
      </header>
      <div className="card-content p-0">
        <div className="card-surface">
          <div className="columns is-variable is-1 px-2 pt-2 mb-0">
            <div className="column is-half pr-2">
              <div className="mb-1 has-text-weight-medium">Assign</div>
              {driverSelect}
            </div>

            <div className="column is-half pl-2">
              <div className="mb-1 has-text-weight-medium">Group</div>
              <DeliveryGroupSelect
                groups={filterDeliveryGroups(dashboard.groups, group.id)}
                allowUngroup={true}
                isUnallocatedOrder={false}
                onConfirm={handleMergeGroups}
              />
            </div>
          </div>
        </div>
        {deliveryGroupDeliveries}
      </div>
    </div>
  );
}

export { GroupComponent };
